import { canManageAccounts, hasAdminAccountAccess as isAdminAccountAccess } from "../../../utils/accountManagementPermissions"
import React, { useEffect, useState } from "react"
import api from "../../../services/api"
import Modal from '../index'
import ModalConfirm from '../ModalConfirm'
import Input from '../../Form/Input'
import FormGroup from "../../Form/FormGroup"
import Button from "../../Form/Button"
import FormGroupInline from '../../Form/FormGrid'
import FormSection from '../../Form/FormSection'
import OptionCardSelector from '../../Form/OptionCardSelector'
import { FaFacebookMessenger, FaInstagram, FaPencilAlt, FaWhatsapp } from "react-icons/fa"
import { HiPlus } from 'react-icons/hi'
import Select from '../../Form/Select'
import { MdAutorenew, MdBusiness, MdChat, MdCheck, MdContacts, MdDashboard, MdExtension, MdLocationOn, MdOutlineAccountCircle, MdOutlineDelete, MdPauseCircleOutline, MdPayments, MdPhone, MdWarningAmber } from "react-icons/md"
import styles from "./modalAccount.module.css"
import { formatCurrency, formatDataSize, maskCurrency, maskDocument, maskPhone, maskZipCode } from "../../../utils/formatter"
import { getAddressByZipCode, getOptionByOptionList, listMethodsPayment } from "../../../utils/utils"
import { useAuth } from "../../../contexts/authContext"
import InfoTooltip from "../../InfoTooltip"
import Textarea from "../../Form/Textarea"

const isEmpty = value => value === undefined || value === null || value.toString().trim() === ""

const ignoredComparisonFields = new Set(["_id", "updated_by", "owner_details", "owner_candidates", "email_owner"])
const numericComparisonFields = new Set([
    "plan_details.discount",
    "plan_details.additional_discount",
    "plan_details.next_invoice_discount",
    "plan_details.additional.users",
    "plan_details.additional.leads",
    "plan_details.additional.teams",
    "commercial_terms.user_unit_price",
    "commercial_terms.complimentary_users",
    "commercial_terms.complimentary_leads",
    "commercial_terms.complimentary_storage_mb",
])
const comparisonDefaults = new Map([
    ["plan_details.discount", 0],
    ["plan_details.additional_discount", 0],
    ["plan_details.next_invoice_discount", 0],
    ["plan_details.additional.users", 0],
    ["plan_details.additional.leads", 0],
    ["plan_details.additional.teams", 0],
    ["commercial_terms.user_unit_price", 0],
    ["commercial_terms.complimentary_users", 0],
    ["commercial_terms.complimentary_leads", 0],
    ["commercial_terms.complimentary_storage_mb", 0],
])
const normalizeComparisonValue = value => {
    if (value === undefined || value === null) return null
    if (Array.isArray(value)) return value.map(normalizeComparisonValue)
    if (typeof value === "object") {
        return Object.keys(value).filter(key => !ignoredComparisonFields.has(key)).sort().reduce((result, key) => {
            const child = normalizeComparisonValue(value[key])
            return child === null ? result : { ...result, [key]: child }
        }, {})
    }
    return value
}

const normalizeFieldComparisonValue = (path, value) => {
    const valueWithDefault = (value === null || value === undefined || value === "") && comparisonDefaults.has(path)
        ? comparisonDefaults.get(path)
        : value
    if (!numericComparisonFields.has(path) && !/^commercial_terms\.additional_charges\.\d+\.amount$/.test(path)) return valueWithDefault
    const numericValue = Number(valueWithDefault)
    return Number.isFinite(numericValue) ? numericValue : valueWithDefault
}

const getScopeSnapshot = (account = {}, scope) => {
    if (scope === "company") return { name: account?.name, owner: account?.owner, company: account?.company }
    if (scope === "contacts") return { contacts: account?.contacts }
    if (scope === "address") return { address: account?.address }
    if (scope === "plan") return { plan_details: account?.plan_details, commercial_terms: account?.commercial_terms }
    if (scope === "settings:modules") return { modules: [...(account?.modules || [])].map(module => module?.module_id || module).map(String).sort() }
    if (scope === "settings:chat") return { chat: account?.chat }
    if (scope === "settings:voip") return { voip: account?.voip }
    if (scope === "settings:webhooks") return { webhooks: account?.webhooks }
    if (scope === "settings:partner") return {
        is_partner: account?.is_partner,
        payment_for: account?.payment_for,
        payment_by_user: account?.payment_by_user,
        partner_permission: account?.partner_permission,
        partner_of: account?.partner_of,
    }
    return {}
}

const collectChanges = (before, after, path = "") => {
    const normalizedBefore = normalizeComparisonValue(before)
    const normalizedAfter = normalizeComparisonValue(after)
    if (JSON.stringify(normalizedBefore) === JSON.stringify(normalizedAfter)) return []
    if (Array.isArray(normalizedBefore) || Array.isArray(normalizedAfter)) {
        const beforeList = Array.isArray(normalizedBefore) ? normalizedBefore : []
        const afterList = Array.isArray(normalizedAfter) ? normalizedAfter : []
        const containsObjects = [...beforeList, ...afterList].some(item => item && typeof item === "object" && !Array.isArray(item))
        if (!containsObjects) return [{ path, before: normalizedBefore, after: normalizedAfter }]
        return Array.from({ length: Math.max(beforeList.length, afterList.length) }, (_, index) => index)
            .flatMap(index => collectChanges(beforeList[index], afterList[index], `${path}.${index}`))
    }
    if ((normalizedBefore && typeof normalizedBefore === "object") || (normalizedAfter && typeof normalizedAfter === "object")) {
        const beforeObject = normalizedBefore && typeof normalizedBefore === "object" ? normalizedBefore : {}
        const afterObject = normalizedAfter && typeof normalizedAfter === "object" ? normalizedAfter : {}
        return [...new Set([...Object.keys(beforeObject), ...Object.keys(afterObject)])]
            .flatMap(key => collectChanges(beforeObject[key], afterObject[key], path ? `${path}.${key}` : key))
    }
    if (Object.is(normalizeFieldComparisonValue(path, normalizedBefore), normalizeFieldComparisonValue(path, normalizedAfter))) return []
    return [{ path, before: normalizedBefore, after: normalizedAfter }]
}

const confirmationFieldLabels = {
    name: "Nome da conta",
    owner: "Responsável",
    "plan_details.plan_id": "Plano",
    "plan_details.automatic_renovation": "Renovação automática",
    "plan_details.discount": "Desconto legado",
    "plan_details.additional_discount": "Desconto adicional",
    "plan_details.additional.users": "Limite total de usuários",
    "plan_details.additional.leads": "Leads adicionais",
    "plan_details.additional.teams": "Equipes adicionais",
    "commercial_terms.pricing_mode": "Modelo de preço",
    "commercial_terms.user_unit_price": "Valor por usuário",
    "commercial_terms.complimentary_users": "Usuários cortesia",
    "commercial_terms.complimentary_leads": "Leads cortesia",
    "commercial_terms.complimentary_storage_mb": "Armazenamento cortesia",
    partner_of: "Conta responsável",
    partner_permission: "Permissão do parceiro",
    is_partner: "Conta parceira",
}

const getConfirmationFieldLabel = path => {
    const chargeMatch = path.match(/^commercial_terms\.additional_charges\.(\d+)\.(description|amount)$/)
    if (chargeMatch) return `Cobrança ${Number(chargeMatch[1]) + 1} · ${chargeMatch[2] === "description" ? "Descrição" : "Valor"}`
    return confirmationFieldLabels[path] || path.split(".").pop()
}

const requiresCommercialReason = path => path === "plan_details.plan_id" || path === "plan_details.discount" || path === "plan_details.additional_discount" || path.startsWith("plan_details.additional.") || (path.startsWith("commercial_terms.") && !["commercial_terms.reason", "commercial_terms.effective_from"].includes(path))
const isCriticalChange = path => requiresCommercialReason(path) || path === "owner" || path === "plan_details.automatic_renovation" || path === "commercial_terms.reason" || ["is_partner", "partner_of", "partner_permission"].includes(path)

const getInitialAccount = (value = {}, isNew = false) => {
    if (!isNew) return value
    return {
        ...value,
        company: { ...value?.company, is_foreigner: value?.company?.is_foreigner ?? false },
        plan_details: {
            ...value?.plan_details,
            when_issue_invoice: value?.plan_details?.when_issue_invoice || "AFTER_PAYMENT",
            automatic_renovation: value?.plan_details?.automatic_renovation ?? true,
        },
        commercial_terms: {
            ...value?.commercial_terms,
            pricing_mode: value?.commercial_terms?.pricing_mode || "PER_USER",
            complimentary_users: value?.commercial_terms?.complimentary_users ?? 0,
            complimentary_leads: value?.commercial_terms?.complimentary_leads ?? 0,
            complimentary_storage_mb: value?.commercial_terms?.complimentary_storage_mb ?? 0,
            additional_charges: value?.commercial_terms?.additional_charges || [],
        },
    }
}

const getCreationValidation = ({ account = {}, contacts = [], paymentByUser = [], modules = [], requiresPartner = false }) => {
    const company = {
        name: isEmpty(account?.name),
        email_owner: isEmpty(account?.email_owner),
        document: isEmpty(account?.company?.document),
        cityCode: isEmpty(account?.company?.cityCode),
        business_name: isEmpty(account?.company?.business_name),
        fantasy_name: isEmpty(account?.company?.fantasy_name),
    }
    const contactFields = contacts.map(contact => ({
        name: isEmpty(contact?.name),
        phone: isEmpty(contact?.phone),
        email: isEmpty(contact?.email),
        department: isEmpty(contact?.department),
    }))
    const contactCount = contactFields.reduce((total, fields) => total + Object.values(fields).filter(Boolean).length, 0) || (!contacts.length ? 1 : 0)
    const address = {
        zip_code: isEmpty(account?.address?.zip_code),
        country: isEmpty(account?.address?.country),
        uf: isEmpty(account?.address?.uf),
        city: isEmpty(account?.address?.city),
        district: isEmpty(account?.address?.district),
        road: isEmpty(account?.address?.road),
        number: isEmpty(account?.address?.number),
    }
    const plan = {
        plan_id: isEmpty(account?.plan_details?.plan_id),
        default_method: isEmpty(account?.plan_details?.default_method),
        user_unit_price: account?.commercial_terms?.pricing_mode === "PER_USER" && !(Number(account?.commercial_terms?.user_unit_price) > 0),
    }
    const additionalChargeFields = (account?.commercial_terms?.additional_charges || []).map(charge => ({
        description: isEmpty(charge?.description),
        amount: !(Number(charge?.amount) > 0),
    }))
    const additionalChargeCount = additionalChargeFields.reduce((total, fields) => total + Object.values(fields).filter(Boolean).length, 0)
    const selectedModuleActions = modules
        .filter(module => (account?.modules || []).some(moduleId => moduleId?.toString() === module.id?.toString()))
        .map(module => `${module?.action || module?.value || ""}`.toUpperCase())
    const usesVoip = selectedModuleActions.some(action => action.includes("VOIP") || action.includes("PHONE"))
    const tierErrors = Boolean(+account?.is_partner) && account?.payment_for === "USER"
        ? paymentByUser.map(tier => ({ end_amount: !(Number(tier?.end_amount) > 0), value: !(Number(tier?.value) > 0) }))
        : []
    const settings = {
        partner_of: requiresPartner && isEmpty(account?.partner_of),
        payment_tiers: tierErrors.reduce((total, fields) => total + Object.values(fields).filter(Boolean).length, 0),
        voip_cost: usesVoip && !Boolean(+account?.voip?.is_progressive_cost) && !(Number(account?.voip?.cost_per_minute) > 0),
    }
    const counts = {
        company: Object.values(company).filter(Boolean).length,
        contacts: contactCount,
        address: Object.values(address).filter(Boolean).length,
        plan: Object.values(plan).filter(Boolean).length + additionalChargeCount,
        settings: Number(settings.partner_of) + settings.payment_tiers + Number(settings.voip_cost),
    }
    return { company, contactFields, address, plan, additionalChargeFields, settings, tierErrors, counts, total: Object.values(counts).reduce((sum, count) => sum + count, 0) }
}

const EMPTY_PLANS = []

const ModalAccount = ({ isNew = false, value = {}, modules = [], plans = EMPTY_PLANS, partnersAccounts = [], editable: initiallyEditable = false, canEdit: canEditRequested = true, setEdit, onSubmit, closeModal }) => {
    const [account, setAccount] = useState(() => getInitialAccount(value, isNew))
    const [contacts, setContacts] = useState(value?.contacts || [{}])
    const [paymentByUser, setPaymentByUser] = useState(value?.payment_by_user || [{}])
    const [modulesByPlan, setModulesByPlan] = useState([])
    const [activeStep, setActiveStep] = useState("company")
    const [activeResource, setActiveResource] = useState("modules")
    const [editingScope, setEditingScope] = useState(isNew || initiallyEditable ? "company" : null)
    const [pendingNavigation, setPendingNavigation] = useState(null)
    const [pendingSubmit, setPendingSubmit] = useState(null)
    const [changeReason, setChangeReason] = useState("")
    const [showValidation, setShowValidation] = useState(false)
    const [optionsPlans] = useState(plans?.map(plan => { return { id: plan._id, value: plan.name } }))

    const { permissions } = useAuth()
    const hasAdminAccountAccess = isAdminAccountAccess(permissions)
    const canEdit = canEditRequested && canManageAccounts(permissions)

    useEffect(() => {
        handleChangeValue("contacts", contacts)
    }, [contacts])

    useEffect(() => {
        const plan = getPlanByPlanId(account?.plan_details?.plan_id)
        const modulesByPlan = plan?.modules?.map(module => module.module_id) || []
        setModulesByPlan(modulesByPlan)
        setAccount(current => ({ ...current, modules: [...new Set([...modulesByPlan, ...(current?.modules || [])].map(String))] }))
    }, [account?.plan_details?.plan_id, plans])

    useEffect(() => {
        handleChangeValue("payment_by_user", paymentByUser)
    }, [paymentByUser])

    const optionsDepartment = [
        { id: "Administrativo", value: "Administrativo" },
        { id: "Financeiro", value: "Financeiro" },
        { id: "Marketing", value: "Marketing" },
        { id: "Tecnologia da Informação", value: "Tecnologia da Informação" },
        { id: "Outro", value: "Outro" },
    ]

    const optionsPermissionsPartner = [
        { id: "NOT_ACCESS", value: "Sem Acesso" },
        { id: "READ_ONLY", value: "Somente leitura" },
        { id: "ONLY_ESSENTIAL", value: "Somente alterações essenciais" },
        { id: "FULL_ACCESS", value: "Acesso total" },
    ]

    const optionsBoolean = [
        { id: 1, value: "Sim" },
        { id: 0, value: "Não" },
    ]

    const optionsPaymentFor = [
        { id: 'ACCOUNT', value: "Conta Ativa" },
        { id: 'USER', value: "Usuário Ativo" },
    ]

    const optionsPlatform = [
        { id: "whatsapp_api", value: "WhatsApp API", description: "API oficial da Meta" },
        { id: "whatsapp", value: "WhatsApp Lite", description: "Conexão simplificada por dispositivo" },
        { id: "direct", value: "Instagram Direct", description: "Mensagens recebidas pelo Instagram" },
        { id: "messenger", value: "Messenger", description: "Mensagens das páginas do Facebook" },
    ]

    const optionsAutomaticRenewal = [
        { id: "1", value: "Renovação ativada", description: "As próximas mensalidades serão geradas automaticamente." },
        { id: "0", value: "Renovação desativada", description: "A licença permanece válida até o vencimento atual." },
    ]

    const optionsWhenIssueInvoice = [
        { id: "AFTER_PAYMENT", value: "Após o pagamento" },
        { id: "ON_CHARGE", value: "Na emissão da cobrança" },
    ]

    const optionsPricingMode = [
        { id: "PLAN_DEFAULT", value: "Preço padrão do plano" },
        { id: "PER_USER", value: "Preço negociado por usuário" },
    ]

    const getDataByCNPJ = async (cnpj) => {
        try {
            const response = await api.get(`/accounts/cnpj/${cnpj}`)
            const data = response?.data?.data
            const business_name = data?.business_name
            const fantasy_name = data?.fantasy_name
            const cityCode = data?.cityCode
            if (business_name) {
                handleChangeCompany("business_name", business_name)
            }
            if (fantasy_name) {
                handleChangeCompany("fantasy_name", fantasy_name)
            }
            if (cityCode) {
                handleChangeCompany("cityCode", cityCode)
            }
        } catch (err) {
            console.log(err)
        }
    }

    const handleChangeValue = (name, value) => {
        setAccount(previousValue => { return { ...previousValue, [name]: value } })
    }

    const handleChangeCompany = (name, value) => {
        if (name === "document" && value.length >= 14) {
            getDataByCNPJ(value)
        }
        setAccount(previousValue => {
            const updatedAccount = { ...previousValue }
            updatedAccount.company = { ...updatedAccount.company }
            updatedAccount.company[name] = value
            return updatedAccount
        })
    }

    const handleChangeAddress = (name, value) => {
        setAccount(previousValue => {
            const updatedAccount = { ...previousValue }
            updatedAccount.address = { ...updatedAccount.address }
            updatedAccount.address[name] = value
            return updatedAccount
        })
        if (name === "zip_code") {
            getAddressByZipCode(value).then(newAddress => {
                Object.keys(newAddress)?.forEach(key => {
                    handleChangeAddress(key, newAddress[key])
                })
            })
        }
    }

    const handleChangeContacts = (name, value, index) => {
        setContacts(previousValue => previousValue?.map((contact, i) => {
            if (i === index) {
                return { ...contact, [name]: value }
            }
            return contact
        }))
    }

    const handleChangePaymentUser = (name, value, index) => {
        if (name === "end_amount") {
            const newName = "start_amount"
            const newValue = parseInt(value) + 1
            const newIndex = index + 1
            handleChangePaymentUser(newName, newValue, newIndex)
        }

        setPaymentByUser(previousValue => previousValue?.map((paymentUser, i) => {
            if (i === index) {
                return { ...paymentUser, [name]: value }
            }
            return paymentUser
        }))
    }

    const handleChangePlan = (name, value) => {
        setAccount(previousValue => {
            const updatedAccount = { ...previousValue }
            updatedAccount.plan_details = { ...updatedAccount.plan_details, [name]: value }
            if (isNew && name === "plan_id") {
                const plan = getPlanByPlanId(value)
                const unitPrice = plan?.user_unit_price ?? (plan?.value == null ? null : Number(plan.value) / Math.max(1, Number(plan?.limits?.users) || 1))
                updatedAccount.commercial_terms = {
                    ...previousValue.commercial_terms,
                    user_unit_price: unitPrice == null ? "" : Number(unitPrice).toFixed(2),
                }
            }
            return updatedAccount
        })
    }

    const getModuleIcon = (module) => {
        const action = `${module?.action || module?.value || ""}`.toUpperCase()
        if (action.includes("CHAT")) return <MdChat />
        if (action.includes("VOIP") || action.includes("PHONE")) return <MdPhone />
        if (action.includes("CRM")) return <MdDashboard />
        if (action.includes("FINANCE")) return <MdPayments />
        return <MdExtension />
    }

    const getPlatformIcon = (platform) => {
        if (platform?.id === "direct") return <FaInstagram />
        if (platform?.id === "messenger") return <FaFacebookMessenger />
        return <FaWhatsapp />
    }

    const handleChangeCommercialTerms = (name, value) => {
        setAccount(previousValue => ({
            ...previousValue,
            commercial_terms: { ...previousValue?.commercial_terms, [name]: value },
        }))
    }

    const handleAddAdditionalCharge = () => {
        if ((account?.commercial_terms?.additional_charges || []).length >= 20) return
        setAccount(previousValue => ({
            ...previousValue,
            commercial_terms: {
                ...previousValue?.commercial_terms,
                additional_charges: [...(previousValue?.commercial_terms?.additional_charges || []), { description: "", amount: "" }],
            },
        }))
    }

    const handleChangeAdditionalCharge = (index, name, value) => {
        setAccount(previousValue => ({
            ...previousValue,
            commercial_terms: {
                ...previousValue?.commercial_terms,
                additional_charges: (previousValue?.commercial_terms?.additional_charges || []).map((charge, chargeIndex) =>
                    chargeIndex === index ? { ...charge, [name]: value } : charge
                ),
            },
        }))
    }

    const handleRemoveAdditionalCharge = index => {
        setAccount(previousValue => ({
            ...previousValue,
            commercial_terms: {
                ...previousValue?.commercial_terms,
                additional_charges: (previousValue?.commercial_terms?.additional_charges || []).filter((_, chargeIndex) => chargeIndex !== index),
            },
        }))
    }

    const handleChangeWebhookSettings = (name, value) => {
        setAccount(previousValue => ({
            ...previousValue,
            webhooks: { ...previousValue?.webhooks, [name]: value },
        }))
    }

    const handleChangePlanDetailsAdditional = (name, value) => {
        setAccount(previousValue => {
            const updatedAccount = { ...previousValue }
            updatedAccount.plan_details = { ...updatedAccount.plan_details }
            updatedAccount.plan_details.additional = { ...updatedAccount?.plan_details?.additional, [name]: value }
            return updatedAccount
        })
    }

    const handleChangePlanAdditional = (name, value) => {
        if (name === "users") {
            const [users] = getLimitUsersByAccount(true)
            const newAdditionalUsers = value < users ? 0 : value - users
            handleChangePlanDetailsAdditional("users", newAdditionalUsers)
        } else if (name === "leads") {
            const [leads, , complimentaryLeads] = getLimitLeadsByAccount(true)
            const includedLeads = leads + complimentaryLeads
            const requestedAdditionalLeads = value < includedLeads ? 0 : value - includedLeads
            const newAdditionalLeads = requestedAdditionalLeads > 0 ? Math.ceil(requestedAdditionalLeads / 1000) * 1000 : 0
            handleChangePlanDetailsAdditional("leads", newAdditionalLeads)
        } else if (name === "teams") {
            const [teams] = getLimitTeamsByAccount(true)
            const newAdditionalLeads = value < teams ? 0 : value - teams
            handleChangePlanDetailsAdditional("teams", newAdditionalLeads)
        }
    }

    const handleChangeVoip = (name, value) => {
        setAccount(previousValue => {
            const updatedAccount = { ...previousValue }
            updatedAccount.voip = { ...updatedAccount.voip }
            updatedAccount.voip[name] = value
            return updatedAccount
        })
    }

    const handleChangeChat = (name, value) => {
        setAccount(previousValue => {
            const updatedAccount = { ...previousValue }
            updatedAccount.chat = { ...updatedAccount.chat }
            updatedAccount.chat[name] = value
            return updatedAccount
        })
    }

    const handleAddNewContact = (e) => {
        e.preventDefault()
        setContacts(previousValue => { return [...previousValue, {}] })
    }

    const handleRemoveContact = (e, index) => {
        e.preventDefault()
        setContacts(previousValue => previousValue?.filter((_, i) => i !== index))
    }

    const handleAddNewPaymentUser = (e) => {
        e.preventDefault()
        setPaymentByUser(previousValue => { return [...previousValue, {}] })
    }

    const handleRemovePaymentUser = (e, index) => {
        e.preventDefault()
        setPaymentByUser(previousValue => previousValue?.filter((_, i) => i !== index))
    }

    const resetDraft = () => {
        setAccount({ ...value, modules: [...new Set([...modulesByPlan, ...(value?.modules || [])].map(String))] })
        setContacts(value?.contacts || [{}])
        setPaymentByUser(value?.payment_by_user || [{}])
    }

    const stopEditing = () => {
        if (isNew || !editingScope) return
        resetDraft()
        setEditingScope(null)
        setEdit?.(false)
    }

    const handleCancel = (e) => {
        e.preventDefault()
        stopEditing()
    }

    const getValueBySelectedPlan = () => {
        const plan = getPlanByPlanId(account?.plan_details?.plan_id)
        if (!plan) {
            return "Nenhum plano selecionado!"
        }
        if (plan?.payment_by_partner) {
            return "Preço para parceiros!"
        }

        const pricingMode = account?.commercial_terms?.pricing_mode || "PLAN_DEFAULT"
        const value = parseFloat(plan?.value) || 0
        const discount = parseFloat(account?.plan_details?.discount) || 0
        const additionalDiscount = parseFloat(account?.plan_details?.additional_discount) || 0
        const additionalUsers = parseFloat(account?.plan_details?.additional?.users) || 0
        const additionalLeads = parseFloat(account?.plan_details?.additional?.leads) || 0
        const additionalUsersCost = parseFloat(plan?.additional_cost?.users) || 0
        const additionalLeadsCost = parseFloat(plan?.additional_cost?.leads) || 0
        const totalUsers = (parseFloat(plan?.limits?.users) || 0) + additionalUsers
        const complimentaryUsers = Math.min(totalUsers, Math.max(0, parseInt(account?.commercial_terms?.complimentary_users) || 0))
        const billableUsers = Math.max(0, totalUsers - complimentaryUsers)
        const negotiatedUnitPrice = parseFloat(account?.commercial_terms?.user_unit_price) || 0
        const usersCost = pricingMode === "PER_USER" ? billableUsers * negotiatedUnitPrice : additionalUsers * additionalUsersCost
        const leadsCost = Math.ceil(additionalLeads / 1000) * additionalLeadsCost
        const additionalChargesCost = (account?.commercial_terms?.additional_charges || [])
            .reduce((total, charge) => total + (parseFloat(charge?.amount) || 0), 0)
        const fullValue = pricingMode === "PER_USER"
            ? usersCost + leadsCost + additionalChargesCost
            : (value + usersCost + leadsCost + additionalChargesCost) - (discount + additionalDiscount)
        return formatCurrency(fullValue > 0 ? fullValue : 0)
    }

    const getLimitUsersByAccount = (array = false) => {
        const plan = getPlanByPlanId(account?.plan_details?.plan_id)
        const users = plan?.limits?.users || 0
        const additionalUsers = account?.plan_details?.additional?.users || 0
        if (array) {
            return [users, additionalUsers]
        }
        return users + additionalUsers
    }

    const getLimitLeadsByAccount = (array = false) => {
        const plan = getPlanByPlanId(account?.plan_details?.plan_id)
        const users = getLimitUsersByAccount()
        const leads = plan?.payment_by_user ? (plan?.limits?.leads || 0) * users : plan?.limits?.leads || 0
        const additionalLeads = account?.plan_details?.additional?.leads || 0
        const complimentaryLeads = Math.max(0, parseInt(account?.commercial_terms?.complimentary_leads) || 0)
        if (array) {
            return [leads, additionalLeads, complimentaryLeads]
        }
        return leads + additionalLeads + complimentaryLeads
    }

    const getLimitTeamsByAccount = (array = false) => {
        const plan = getPlanByPlanId(account?.plan_details?.plan_id)
        const teams = plan?.limits?.teams || 0
        const additionalTeams = account?.plan_details?.additional?.teams || 0
        if (array) {
            return [teams, additionalTeams]
        }
        return teams + additionalTeams
    }

    const getLimitStorageByAccount = () => {
        const plan = getPlanByPlanId(account?.plan_details?.plan_id)
        const users = getLimitUsersByAccount()
        const storagePerUser = plan?.limits?.storage_per_user || 0
        const complimentaryStorage = Math.max(0, parseInt(account?.commercial_terms?.complimentary_storage_mb) || 0)
        const storage = (users * storagePerUser) + complimentaryStorage
        return formatDataSize(storage)
    }

    const getPlanByPlanId = (plan_id) => {
        if (!plan_id) {
            return
        }
        const plan = plans.find(plan => plan._id === plan_id)
        return plan
    }

    const steps = [
        { id: "company", label: "Empresa", description: "Identificação e responsável", icon: <MdBusiness /> },
        { id: "contacts", label: "Contatos", description: "Pessoas e departamentos", icon: <MdContacts /> },
        { id: "address", label: "Endereço", description: "Dados fiscais e localização", icon: <MdLocationOn /> },
        { id: "plan", label: "Plano e valores", description: "Contrato, limites e cobrança", icon: <MdPayments /> },
        { id: "settings", label: "Recursos", description: "Módulos e configurações", icon: <MdExtension /> },
    ]
    const resourceTabs = [
        { id: "modules", label: "Módulos" },
        { id: "chat", label: "Chat" },
        { id: "voip", label: "VoIP" },
        { id: "webhooks", label: "Webhooks" },
        { id: "partner", label: "Parceria" },
    ]
    const activeStepIndex = steps.findIndex(step => step.id === activeStep)
    const activeEditingScope = activeStep === "settings" ? `settings:${activeResource}` : activeStep
    const canEditActiveScope = canEdit && (activeStep !== "settings" || hasAdminAccountAccess)
    const editable = canEditActiveScope && (isNew || editingScope === activeEditingScope)
    const selectedPlan = getPlanByPlanId(account?.plan_details?.plan_id)
    const visibleResourceTabs = resourceTabs.filter(tab => tab.id === "modules" || hasAdminAccountAccess)
    const accountDisplayName = account?.name || account?.company?.fantasy_name || "Nova conta"
    const accountInitial = accountDisplayName.trim().charAt(0).toUpperCase() || "N"
    const activeResourceLabel = resourceTabs.find(tab => tab.id === activeResource)?.label
    const ownerOptions = (account?.owner_candidates || []).map(user => ({
        id: user._id,
        value: user?.name ? `${user.name} — ${user.email}` : user.email,
    }))
    const currentOwnerLabel = account?.owner_details?.name
        ? `${account.owner_details.name} — ${account.owner_details.email}`
        : account?.owner_details?.email || account?.email_owner || "-"
    const creationValidation = getCreationValidation({ account, contacts, paymentByUser, modules, requiresPartner: hasAdminAccountAccess })
    const scopeChanges = isNew ? [] : collectChanges(getScopeSnapshot(value, activeEditingScope), getScopeSnapshot(account, activeEditingScope))
    const hasUnsavedChanges = scopeChanges.length > 0
    const criticalChanges = scopeChanges.filter(change => isCriticalChange(change.path))
    const mustProvideReason = scopeChanges.some(change => requiresCommercialReason(change.path))
    const completedSteps = steps.length - Object.values(creationValidation.counts).filter(count => count > 0).length
    const fieldValidationClass = hasError => isNew && hasError ? (showValidation ? styles.fieldError : styles.fieldPending) : ""

    const startEditing = () => {
        setEditingScope(activeEditingScope)
        setEdit?.(true)
    }

    const prepareScopeNavigation = nextScope => {
        if (!isNew && editingScope) {
            resetDraft()
            setEditingScope(nextScope)
        }
    }

    const selectStep = (stepId) => {
        if (stepId === activeStep) return
        if (!isNew && editingScope && hasUnsavedChanges) {
            setPendingNavigation({ type: "step", id: stepId })
            return
        }
        prepareScopeNavigation(stepId === "settings" ? `settings:${activeResource}` : stepId)
        setActiveStep(stepId)
    }

    const selectResource = (resourceId) => {
        if (resourceId === activeResource) return
        if (!isNew && editingScope && hasUnsavedChanges) {
            setPendingNavigation({ type: "resource", id: resourceId })
            return
        }
        prepareScopeNavigation(`settings:${resourceId}`)
        setActiveResource(resourceId)
    }

    const confirmNavigation = () => {
        const navigation = pendingNavigation
        prepareScopeNavigation(navigation?.type === "resource" ? `settings:${navigation.id}` : navigation?.id === "settings" ? `settings:${activeResource}` : navigation?.id)
        setPendingNavigation(null)
        if (navigation?.type === "step") setActiveStep(navigation.id)
        if (navigation?.type === "resource") setActiveResource(navigation.id)
    }

    const pendingDestination = pendingNavigation?.type === "step"
        ? steps.find(step => step.id === pendingNavigation.id)?.label
        : resourceTabs.find(tab => tab.id === pendingNavigation?.id)?.label

    const goToPreviousStep = () => setActiveStep(steps[Math.max(0, activeStepIndex - 1)].id)
    const goToNextStep = () => setActiveStep(steps[Math.min(steps.length - 1, activeStepIndex + 1)].id)

    const handleSubmit = (event) => {
        event.preventDefault()
        if (!canEdit || (!isNew && !editable)) return
        if (isNew && activeStepIndex < steps.length - 1) {
            goToNextStep()
            return
        }
        if (isNew && creationValidation.total > 0) {
            setShowValidation(true)
            const firstInvalidStep = steps.find(step => creationValidation.counts[step.id] > 0)
            if (firstInvalidStep) {
                setActiveStep(firstInvalidStep.id)
                if (firstInvalidStep.id === "settings") {
                    setActiveResource(creationValidation.settings.partner_of || creationValidation.settings.payment_tiers > 0 ? "partner" : "voip")
                }
            }
            return
        }
        const payload = isNew ? account : { ...account, update_scope: activeEditingScope }
        if (!isNew && !hasUnsavedChanges) return
        if (!isNew && criticalChanges.length) {
            setChangeReason("")
            setPendingSubmit(payload)
            return
        }
        onSubmit(event, isNew, payload)
    }

    const formatConfirmationValue = (path, fieldValue, sourceAccount = account) => {
        if ((fieldValue === null || fieldValue === undefined || fieldValue === "") && path === "plan_details.automatic_renovation") return "Ativada"
        if ((fieldValue === null || fieldValue === undefined || fieldValue === "") && path === "commercial_terms.pricing_mode") return "Preço padrão do plano"
        if (fieldValue === null || fieldValue === undefined || fieldValue === "") return "Não informado"
        if (typeof fieldValue === "boolean") return fieldValue ? "Sim" : "Não"
        if (path === "owner") {
            const users = [value?.owner_details, account?.owner_details, ...(value?.owner_candidates || []), ...(account?.owner_candidates || [])].filter(Boolean)
            const user = users.find(item => item?._id?.toString() === fieldValue?.toString())
            return user?.name ? `${user.name} — ${user.email}` : user?.email || fieldValue.toString()
        }
        if (path === "plan_details.plan_id") return plans.find(plan => plan?._id?.toString() === fieldValue?.toString())?.name || fieldValue.toString()
        if (path === "plan_details.additional.users") {
            const planId = sourceAccount?.plan_details?.plan_id
            const includedUsers = Number(plans.find(plan => plan?._id?.toString() === planId?.toString())?.limits?.users) || 0
            return (includedUsers + (Number(fieldValue) || 0)).toString()
        }
        if (path === "commercial_terms.pricing_mode") return getOptionByOptionList(fieldValue, optionsPricingMode) || fieldValue.toString()
        if (path === "partner_permission") return getOptionByOptionList(fieldValue, optionsPermissionsPartner) || fieldValue.toString()
        if (["commercial_terms.user_unit_price", "plan_details.discount", "plan_details.additional_discount"].includes(path) || /^commercial_terms\.additional_charges\.\d+\.amount$/.test(path)) return formatCurrency(fieldValue)
        if (Array.isArray(fieldValue)) return fieldValue.length ? fieldValue.join(", ") : "Nenhum"
        return fieldValue.toString()
    }

    const confirmCriticalSubmit = () => {
        if (!pendingSubmit || (mustProvideReason && !changeReason.trim())) return
        const payload = { ...pendingSubmit, change_reason: changeReason.trim() || undefined }
        setPendingSubmit(null)
        onSubmit({ preventDefault: () => {} }, false, payload)
    }

    return (<>
        <Modal
            icon={<MdOutlineAccountCircle />}
            title={isNew ? "Cadastro de nova conta" : (editable ? `Editando a conta: ${account.name}` : `Visualizando: ${account.name}`)}
            description={isNew ? "Preencha uma etapa por vez" : (editable ? `Editando somente: ${steps[activeStepIndex]?.label}${activeStep === "settings" ? ` — ${activeResourceLabel}` : ""}` : (canEdit ? `Escolha uma seção para consultar ou editar` : "Você tem acesso somente para visualização"))}
            closeModal={closeModal}
            fullWidith={true}
            bodyClassName={styles.modalBody}
        >
            <form className={styles.form} onSubmit={handleSubmit}>
                <header className={styles.accountOverview}>
                    <div className={styles.overviewAvatar}>{accountInitial}</div>
                    <div className={styles.overviewIdentity}>
                        <strong>{accountDisplayName}</strong>
                        <span>{account?.company?.business_name || account?.email_owner || "Dados gerais da conta"}</span>
                    </div>
                    <div className={styles.overviewMeta}>
                        <span className={`${styles.statusBadge} ${account?.is_active || isNew ? styles.statusActive : styles.statusInactive}`}>
                            {isNew ? "Em cadastro" : (account?.is_active ? "Conta ativa" : "Conta bloqueada")}
                        </span>
                        <span className={styles.planBadge}>{selectedPlan?.name || "Sem plano"}</span>
                        <div className={styles.overviewValue}>
                            <small>Valor estimado</small>
                            <strong>{getValueBySelectedPlan()}</strong>
                        </div>
                    </div>
                </header>
                <div className={styles.editorLayout}>
                    <aside className={styles.sidebar}>
                        {isNew &&
                            <div className={styles.progressSummary}>
                                <div><span>Configuração</span><strong>{completedSteps} de {steps.length}</strong></div>
                                <div className={styles.progressTrack}><span style={{ width: `${(completedSteps / steps.length) * 100}%` }} /></div>
                            </div>
                        }
                        <nav className={styles.sectionNavigation} aria-label="Seções da conta">
                            {steps.map(step => (
                                <button type="button" className={`${styles.stepButton} ${activeStep === step.id ? styles.activeStep : ""} ${!isNew && activeStep === step.id && hasUnsavedChanges ? styles.changedStep : ""}`} onClick={() => selectStep(step.id)} key={step.id}>
                                    <span className={styles.stepMarker}>{isNew && creationValidation.counts[step.id] === 0 ? <MdCheck /> : step.icon}</span>
                                    <span className={styles.stepText}><strong>{step.label}</strong><small>{step.description}</small></span>
                                    {isNew && creationValidation.counts[step.id] > 0 && <span className={styles.pendingBadge} title={`${creationValidation.counts[step.id]} configurações pendentes`}>{creationValidation.counts[step.id]}</span>}
                                    {!isNew && activeStep === step.id && hasUnsavedChanges && <span className={styles.changedBadge} title={`${scopeChanges.length} alterações não salvas`}>{scopeChanges.length}</span>}
                                </button>
                            ))}
                        </nav>
                    </aside>
                    <main className={styles.editorMain}>
                        <header className={styles.sectionHeader}>
                            <span className={styles.sectionIcon}>{steps[activeStepIndex]?.icon}</span>
                            <div className={styles.sectionHeading}>
                                <small>{isNew ? `Etapa ${activeStepIndex + 1} de ${steps.length}` : "Configuração da conta"}</small>
                                <h3>{steps[activeStepIndex]?.label}</h3>
                                <p>{steps[activeStepIndex]?.description}</p>
                                {!isNew && editable && <span className={`${styles.editState} ${hasUnsavedChanges ? styles.editStateChanged : ""}`}>{hasUnsavedChanges ? `${scopeChanges.length} alteração(ões) não salva(s)` : "Nenhuma alteração"}</span>}
                            </div>
                            <div className={styles.sectionHeaderActions}>
                                {!editable && canEditActiveScope &&
                                    <button type="button" className={`${styles.editSectionButton} ${activeStep === "settings" && activeResource === "modules" ? styles.editModulesButton : ""}`} onClick={startEditing} aria-label={`Editar ${steps[activeStepIndex]?.label}`} title={`Editar ${steps[activeStepIndex]?.label}`}>
                                        <FaPencilAlt />{activeStep === "settings" && activeResource === "modules" && " Editar módulos"}
                                    </button>
                                }
                                {editable && !isNew && <Button variant="primary" type="submit" disabled={!hasUnsavedChanges}>Salvar alterações</Button>}
                                {editable && !isNew && <Button type="button" variant="secondary" onClick={handleCancel}>Cancelar</Button>}
                            </div>
                        </header>
                        <section className={styles.stepContent}>
                {activeStep === "company" && <>
                <FormSection title="Acesso à conta" description="Informações usadas para identificar a conta e seu responsável principal.">
                <FormGroupInline>
                    <FormGroup className={fieldValidationClass(creationValidation.company.name)} labelText="Nome da conta">
                        {editable ?
                            <Input type="text" placeholder="Nome da conta" value={account.name ?? ""} onChange={(e) => handleChangeValue("name", e.target.value)} />
                            :
                            <span>{account.name}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.company.email_owner)} labelText={isNew ? "E-mail do responsável" : "Responsável pela conta"}>
                        {editable && isNew ?
                            <Input type="email" placeholder="E-mail do usuário" value={account.email_owner ?? ""} onChange={(e) => handleChangeValue("email_owner", e.target.value)} />
                            : editable ?
                            <Select
                                multiple={false}
                                allowDeselectAll={false}
                                name="Selecione um usuário ativo..."
                                placeholder="Buscar por nome ou e-mail..."
                                options={ownerOptions}
                                selected={account?.owner?.toString() || ""}
                                onChange={(value) => handleChangeValue("owner", value)}
                                emptyOptionsText="A conta não possui usuários ativos."
                            />
                            :
                            <span>{currentOwnerLabel}</span>
                        }
                    </FormGroup>
                </FormGroupInline>
                </FormSection>
                <FormSection title="Dados da empresa" description="Dados jurídicos e fiscais utilizados em contratos, cobranças e notas fiscais.">
                <FormGroupInline>
                    <FormGroup className={fieldValidationClass(creationValidation.company.document)} labelText="CNPJ/CPF">
                        {editable ?
                            <Input type={+account?.company?.is_foreigner ? "text" : "document"} placeholder="CNPJ/CPF" value={account?.company?.document ?? ""} onChange={(e) => handleChangeCompany("document", e.target.value)} />
                            :
                            <span>{+account?.company?.is_foreigner ? account?.company?.document : maskDocument(account?.company?.document)}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.company.cityCode)} labelText="Código do Município (Código TOM)">
                        {editable ?
                            <Input type="text" placeholder="Código do Município" value={account?.company?.cityCode ?? ""} onChange={(e) => handleChangeCompany("cityCode", e.target.value)} />
                            :
                            <span>{account?.company?.cityCode}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.company.business_name)} labelText="Razão social">
                        {editable ?
                            <Input type="text" placeholder="Razão social" value={account?.company?.business_name ?? ""} onChange={(e) => handleChangeCompany("business_name", e.target.value)} />
                            :
                            <span>{account?.company?.business_name}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.company.fantasy_name)} labelText="Nome fantasia">
                        {editable ?
                            <Input type="text" placeholder="Nome fantasia" value={account?.company?.fantasy_name ?? ""} onChange={(e) => handleChangeCompany("fantasy_name", e.target.value)} />
                            :
                            <span>{account?.company?.fantasy_name}</span>
                        }
                    </FormGroup>
                    <FormGroup className={styles.fullWidthField} labelText="É estrangeiro?">
                        {editable ?
                            <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsBoolean} onChange={(value) => handleChangeCompany("is_foreigner", value)} selected={+account?.company?.is_foreigner ?? '0'} />
                            :
                            <span>{getOptionByOptionList(+account?.company?.is_foreigner || '0', optionsBoolean)}</span>
                        }
                    </FormGroup>
                </FormGroupInline>
                </FormSection>
                </>}
                {activeStep === "contacts" && <>
                <FormSection title="Pessoas de contato" description="Cadastre quem deve ser procurado em cada departamento." action={editable ? <Button type="button" variant="primary" onClick={handleAddNewContact}><HiPlus /> Adicionar contato</Button> : null}>
                {contacts && contacts.map((contact, index) => (
                    <div className={styles.contact} key={index}>
                        <div className={styles.contactHeader}>
                            <div><span>{index + 1}</span><strong>{contact?.name || `Contato ${index + 1}`}</strong></div>
                            {index > 0 && editable && <Button type="button" variant='secondary' className='action' onClick={(e) => handleRemoveContact(e, index)}><MdOutlineDelete /> Remover</Button>}
                        </div>
                        <FormGroupInline>
                            <FormGroup className={fieldValidationClass(creationValidation.contactFields[index]?.name)} labelText="Nome">
                                {editable ?
                                    <Input type="text" placeholder="Nome do contato" value={contact?.name ?? ""} onChange={(e) => handleChangeContacts("name", e.target.value, index)} />
                                    :
                                    <span>{contact?.name}</span>
                                }
                            </FormGroup>
                            <FormGroup className={fieldValidationClass(creationValidation.contactFields[index]?.phone)} labelText="Celular">
                                {editable ?
                                    <Input type="phone" placeholder="Celular do contato" value={contact?.phone ?? ""} onChange={(e) => handleChangeContacts("phone", e.target.value, index)} />
                                    :
                                    <span>{maskPhone(contact?.phone) || '-'}</span>
                                }
                            </FormGroup>
                            <FormGroup className={fieldValidationClass(creationValidation.contactFields[index]?.email)} labelText="E-mail">
                                {editable ?
                                    <Input type="email" placeholder="E-mail do contato" value={contact?.email ?? ""} onChange={(e) => handleChangeContacts("email", e.target.value, index)} />
                                    :
                                    <span>{contact?.email}</span>
                                }
                            </FormGroup>
                            <FormGroup className={fieldValidationClass(creationValidation.contactFields[index]?.department)} labelText="Departamento">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsDepartment} onChange={(value) => handleChangeContacts("department", value, index)} selected={contact?.department ?? []} />
                                    :
                                    <span>{getOptionByOptionList(contact?.department, optionsDepartment)}</span>
                                }
                            </FormGroup>
                        </FormGroupInline>
                    </div>
                ))}
                </FormSection>
                </>}
                {activeStep === "address" && <>
                <FormSection title="Endereço fiscal" description="Ao informar o CEP, os dados disponíveis são preenchidos automaticamente.">
                <FormGroupInline>
                    <FormGroup className={fieldValidationClass(creationValidation.address.zip_code)} labelText="CEP">
                        {editable ?
                            <Input type="zipCode" placeholder="CEP" value={account?.address?.zip_code ?? ""} onChange={(e) => handleChangeAddress("zip_code", e.target.value)} />
                            :
                            <span>{maskZipCode(account?.address?.zip_code)}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.address.country)} labelText="País">
                        {editable ?
                            <Input type="text" placeholder="País" value={account?.address?.country ?? ""} onChange={(e) => handleChangeAddress("country", e.target.value)} />
                            :
                            <span>{account?.address?.country}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.address.uf)} labelText="Estado">
                        {editable ?
                            <Input type="text" placeholder="Estado" value={account?.address?.uf ?? ""} onChange={(e) => handleChangeAddress("uf", e.target.value)} />
                            :
                            <span>{account?.address?.uf}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.address.city)} labelText="Cidade">
                        {editable ?
                            <Input type="text" placeholder="Cidade" value={account?.address?.city ?? ""} onChange={(e) => handleChangeAddress("city", e.target.value)} />
                            :
                            <span>{account?.address?.city}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.address.district)} labelText="Bairro">
                        {editable ?
                            <Input type="text" placeholder="Bairro" value={account?.address?.district ?? ""} onChange={(e) => handleChangeAddress("district", e.target.value)} />
                            :
                            <span>{account?.address?.district}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.address.road)} labelText="Logradouro">
                        {editable ?
                            <Input type="text" placeholder="Logradouro" value={account?.address?.road ?? ""} onChange={(e) => handleChangeAddress("road", e.target.value)} />
                            :
                            <span>{account?.address?.road}</span>
                        }
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.address.number)} labelText="Número">
                        {editable ?
                            <Input type="text" placeholder="Número" value={account?.address?.number ?? ""} onChange={(e) => handleChangeAddress("number", e.target.value)} />
                            :
                            <span>{account?.address?.number}</span>
                        }
                    </FormGroup>
                    <FormGroup labelText="Complemento">
                        {editable ?
                            <Input type="text" placeholder="Complemento" value={account?.address?.complement ?? ""} onChange={(e) => handleChangeAddress("complement", e.target.value)} />
                            :
                            <span>{account?.address?.complement}</span>
                        }
                    </FormGroup>
                </FormGroupInline>
                </FormSection>
                </>}
                {activeStep === "plan" && <>
                <div className={styles.planSummary}>
                    <div>
                        <small>Plano contratado</small>
                        <strong>{selectedPlan?.name || "Nenhum plano selecionado"}</strong>
                        <span>{getLimitUsersByAccount()} usuários · {getLimitLeadsByAccount()} leads</span>
                    </div>
                    <div className={styles.planSummaryValue}>
                        <small>Mensalidade estimada</small>
                        <strong>{getValueBySelectedPlan()}</strong>
                        <span>{(account?.commercial_terms?.pricing_mode || "PLAN_DEFAULT") === "PER_USER" ? "Preço negociado por usuário" : "Preço padrão do plano"}</span>
                    </div>
                </div>
                <FormSection title="Configuração do plano" description="Defina o plano, os limites contratados e as regras operacionais de cobrança.">
                <FormGroupInline>
                    <FormGroup className={fieldValidationClass(creationValidation.plan.plan_id)} labelText="Plano">
                        {editable && (hasAdminAccountAccess || isNew) ?
                            <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsPlans || []} onChange={(value) => handleChangePlan("plan_id", value)} selected={account?.plan_details?.plan_id || ''} />
                            :
                            <span>{getOptionByOptionList(account?.plan_details?.plan_id || '', optionsPlans) || '-'}</span>
                        }
                    </FormGroup>
                    <FormGroup labelText="Valor">
                        {editable ? <div className={styles.calculatedField}>{getValueBySelectedPlan()}</div> : <span>{getValueBySelectedPlan()}</span>}
                    </FormGroup>
                    <FormGroup labelText="Limite total de usuários" labelContent={<InfoTooltip text="Quantidade total de usuários que podem permanecer ativos na conta. A composição incluída no plano é calculada automaticamente." preferredPosition="bottom" />}>
                        {editable && (hasAdminAccountAccess || isNew) ?
                            <Input type="number" min="0" placeholder="Limite total de usuários" value={getLimitUsersByAccount()} onChange={(e) => handleChangePlanAdditional("users", e.target.value)} />
                            :
                            <span>{getLimitUsersByAccount()}</span>
                        }
                    </FormGroup>
                    <FormGroup labelText="Limite de Leads">
                        {editable && hasAdminAccountAccess ?
                            <Input type="number" placeholder="Limite de Leads" value={getLimitLeadsByAccount()} step="1000" onChange={(e) => handleChangePlanAdditional("leads", e.target.value)} />
                            :
                            <span>{getLimitLeadsByAccount()}</span>
                        }
                    </FormGroup>
                    <FormGroup labelText="Limite de Equipes">
                        {editable && hasAdminAccountAccess ?
                            <Input type="number" placeholder="Limite de Equipes" value={getLimitTeamsByAccount()} onChange={(e) => handleChangePlanAdditional("teams", e.target.value)} />
                            :
                            <span>{getLimitTeamsByAccount()}</span>
                        }
                    </FormGroup>
                    <FormGroup labelText="Limite de Armazenamento">
                        {editable ? <div className={styles.calculatedField}>{getLimitStorageByAccount()}</div> : <span>{getLimitStorageByAccount()}</span>}
                    </FormGroup>
                    <FormGroup className={fieldValidationClass(creationValidation.plan.default_method)} labelText="Método de Pagamento Padrão">
                        {editable ?
                            <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={listMethodsPayment || []} onChange={(value) => handleChangePlan("default_method", value)} selected={account?.plan_details?.default_method || ''} />
                            :
                            <span>{getOptionByOptionList(account?.plan_details?.default_method || '', listMethodsPayment) || '-'}</span>
                        }
                    </FormGroup>
                    <FormGroup labelText="Quando emitir a Nota Fiscal?">
                        {editable && hasAdminAccountAccess ?
                            <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsWhenIssueInvoice || []} onChange={(value) => handleChangePlan("when_issue_invoice", value)} selected={account?.plan_details?.when_issue_invoice || ''} />
                            :
                            <span>{getOptionByOptionList(account?.plan_details?.when_issue_invoice || 'AFTER_PAYMENT', optionsWhenIssueInvoice) || '-'}</span>
                        }
                    </FormGroup>
                    <FormGroup className={styles.fullWidthField} labelText="Renovação automática" labelContent={<InfoTooltip text="Quando ativada, o sistema gera as próximas mensalidades automaticamente. Não se aplica a planos pagos pelo parceiro." preferredPosition="bottom" />}>
                        {selectedPlan?.payment_by_partner ?
                            (editable ? <div className={styles.calculatedField}>Gerenciada pelo parceiro responsável</div> : <span>Gerenciada pelo parceiro responsável</span>)
                            :
                            <OptionCardSelector
                                single
                                options={optionsAutomaticRenewal}
                                selected={[(account?.plan_details?.automatic_renovation ?? true) ? "1" : "0"]}
                                editable={editable && hasAdminAccountAccess}
                                onChange={values => handleChangePlan("automatic_renovation", values[0] === "1")}
                                renderIcon={option => option.id === "1" ? <MdAutorenew /> : <MdPauseCircleOutline />}
                            />
                        }
                    </FormGroup>
                </FormGroupInline>
                </FormSection>
                {hasAdminAccountAccess &&
                    <>
                        <FormSection title="Acordo comercial" description="Condições negociadas para esta conta. Cortesias ficam separadas do preço para preservar o valor real por usuário.">
                        <FormGroupInline>
                            <FormGroup labelText="Modelo de preço" labelContent={<InfoTooltip text="No preço por usuário, todos os usuários faturáveis usam o mesmo valor negociado, inclusive os adicionados depois." preferredPosition="bottom" />}>
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." options={optionsPricingMode} onChange={(value) => handleChangeCommercialTerms("pricing_mode", value)} selected={account?.commercial_terms?.pricing_mode || "PLAN_DEFAULT"} />
                                    :
                                    <span>{getOptionByOptionList(account?.commercial_terms?.pricing_mode || "PLAN_DEFAULT", optionsPricingMode)}</span>
                                }
                            </FormGroup>
                            {(account?.commercial_terms?.pricing_mode || "PLAN_DEFAULT") === "PER_USER" &&
                                <FormGroup className={fieldValidationClass(creationValidation.plan.user_unit_price)} labelText="Valor por usuário">
                                    {editable ?
                                        <Input type="currency" placeholder="Valor por usuário" value={account?.commercial_terms?.user_unit_price ?? ""} onChange={(e) => handleChangeCommercialTerms("user_unit_price", e.target.value)} />
                                        :
                                        <span>{formatCurrency(account?.commercial_terms?.user_unit_price || 0)}</span>
                                    }
                                </FormGroup>
                            }
                            {(account?.commercial_terms?.pricing_mode || "PLAN_DEFAULT") === "PLAN_DEFAULT" &&
                                <FormGroup labelText="Desconto legado" labelContent={<InfoTooltip text="Mantido apenas durante a migração das contas antigas. Para novos acordos, prefira preço por usuário e cortesias identificadas." preferredPosition="bottom" />}>
                                    {editable ?
                                        <Input type="currency" placeholder="Desconto" value={account?.plan_details?.discount} onChange={(e) => handleChangePlan("discount", e.target.value)} />
                                        :
                                        <span>{formatCurrency(account?.plan_details?.discount || 0)}</span>
                                    }
                                </FormGroup>
                            }
                            <FormGroup labelText="Usuários cortesia" labelContent={<InfoTooltip text="Usuários liberados sem cobrança. A cortesia fica identificada separadamente e não reduz artificialmente o preço unitário." preferredPosition="bottom" />}>
                                {editable ?
                                    <Input type="number" min="0" placeholder="Quantidade" value={account?.commercial_terms?.complimentary_users ?? 0} onChange={(e) => handleChangeCommercialTerms("complimentary_users", e.target.value)} />
                                    :
                                    <span>{account?.commercial_terms?.complimentary_users || 0}</span>
                                }
                            </FormGroup>
                            <FormGroup labelText="Leads cortesia" labelContent={<InfoTooltip text="Aumenta o limite de leads sem gerar cobrança adicional. Somente administradores podem conceder esta cortesia." preferredPosition="bottom" />}>
                                {editable ?
                                    <Input type="number" min="0" placeholder="Quantidade" value={account?.commercial_terms?.complimentary_leads ?? 0} onChange={(e) => handleChangeCommercialTerms("complimentary_leads", e.target.value)} />
                                    :
                                    <span>{account?.commercial_terms?.complimentary_leads || 0}</span>
                                }
                            </FormGroup>
                            <FormGroup className={styles.fullWidthField} labelText="Armazenamento cortesia (MB)" labelContent={<InfoTooltip text="Adiciona armazenamento ao limite da conta sem alterar a mensalidade. O valor é informado em MB." preferredPosition="bottom" />}>
                                {editable ?
                                    <Input type="number" min="0" placeholder="Quantidade em MB" value={account?.commercial_terms?.complimentary_storage_mb ?? 0} onChange={(e) => handleChangeCommercialTerms("complimentary_storage_mb", e.target.value)} />
                                    :
                                    <span>{formatDataSize(account?.commercial_terms?.complimentary_storage_mb || 0)}</span>
                                }
                            </FormGroup>
                        </FormGroupInline>
                        <div className={styles.subsectionHeader}>
                            <div>
                                <strong>Cobranças adicionais recorrentes</strong>
                                <span>Valores mensais negociados para serviços, desenvolvimentos ou funcionalidades específicas.</span>
                            </div>
                            {editable && <Button type="button" variant="secondary" disabled={(account?.commercial_terms?.additional_charges || []).length >= 20} onClick={handleAddAdditionalCharge}><HiPlus /> Adicionar cobrança</Button>}
                        </div>
                        {!(account?.commercial_terms?.additional_charges || []).length &&
                            <div className={styles.emptyAdditionalCharges}>Nenhuma cobrança adicional configurada.</div>
                        }
                        {(account?.commercial_terms?.additional_charges || []).map((charge, index) => (
                            <div className={styles.additionalCharge} key={charge?._id || index}>
                                <div className={styles.paymentTierHeader}>
                                    <strong>Cobrança {index + 1}</strong>
                                    {editable && <Button type="button" variant="secondary" className="action" onClick={() => handleRemoveAdditionalCharge(index)}><MdOutlineDelete /> Remover</Button>}
                                </div>
                                <FormGroupInline>
                                    <FormGroup className={fieldValidationClass(creationValidation.additionalChargeFields[index]?.description)} labelText="Descrição">
                                        {editable ?
                                            <Input type="text" maxLength={120} placeholder="Ex.: Desenvolvimento personalizado" value={charge?.description ?? ""} onChange={(e) => handleChangeAdditionalCharge(index, "description", e.target.value)} />
                                            :
                                            <span>{charge?.description || "-"}</span>
                                        }
                                    </FormGroup>
                                    <FormGroup className={fieldValidationClass(creationValidation.additionalChargeFields[index]?.amount)} labelText="Valor mensal">
                                        {editable ?
                                            <Input type="currency" placeholder="Valor mensal" value={charge?.amount ?? ""} onChange={(e) => handleChangeAdditionalCharge(index, "amount", e.target.value)} />
                                            :
                                            <span>{formatCurrency(charge?.amount || 0)}</span>
                                        }
                                    </FormGroup>
                                </FormGroupInline>
                            </div>
                        ))}
                        <FormGroupInline>
                            <FormGroup className={styles.fullWidthField} labelText="Observações do acordo">
                                {editable ?
                                    <Textarea rows={3} maxLength={500} placeholder="Registre o contexto, a autorização e o motivo das condições comerciais desta conta." value={account?.commercial_terms?.reason || ""} onChange={(e) => handleChangeCommercialTerms("reason", e.target.value)} />
                                    :
                                    <span>{account?.commercial_terms?.reason || "-"}</span>
                                }
                            </FormGroup>
                        </FormGroupInline>
                        </FormSection>
                    </>
                }
                </>}
                {activeStep === "settings" && <>
                <div className={styles.resourceTabs} role="tablist" aria-label="Tipos de recurso">
                    {visibleResourceTabs.map(tab =>
                        <button key={tab.id} type="button" role="tab" aria-selected={activeResource === tab.id} className={activeResource === tab.id ? styles.activeResourceTab : ""} onClick={() => selectResource(tab.id)}>
                            {tab.label}{!isNew && activeResource === tab.id && hasUnsavedChanges ? ` (${scopeChanges.length})` : ""}
                        </button>
                    )}
                </div>
                {hasAdminAccountAccess && activeResource === "webhooks" &&
                    <>
                        <FormSection title="Entrega e segurança" description="Controle retenção, alertas e quando uma integração deve ser pausada após falhas.">
                        <FormGroupInline>
                            <FormGroup labelText="Permitir eventos brutos (raw)">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsBoolean} onChange={(value) => handleChangeWebhookSettings("raw_events_enabled", value)} selected={+(account?.webhooks?.raw_events_enabled === true || Number(account?.webhooks?.raw_events_enabled) === 1)} />
                                    :
                                    <span>{account?.webhooks?.raw_events_enabled ? "Sim" : "Não"}</span>
                                }
                            </FormGroup>
                            <FormGroup labelText="Retenção do histórico (dias)" labelContent={<InfoTooltip text="Define por quantos dias os payloads, respostas, headers e tentativas das entregas ficam disponíveis. Após esse prazo, o registro é removido automaticamente. O webhook e suas configurações não são excluídos." preferredPosition="bottom" />}>
                                {editable ?
                                    <Input type="number" min="1" max="365" value={account?.webhooks?.retention_days ?? 30} onChange={(e) => handleChangeWebhookSettings("retention_days", e.target.value)} />
                                    :
                                    <span>{account?.webhooks?.retention_days || 30} dias</span>
                                }
                            </FormGroup>
                            <FormGroup labelText="Pausar após entregas com falha" labelContent={<InfoTooltip text="Conta somente entregas reais que esgotaram todas as tentativas. Testes e tentativas intermediárias não entram no limite. Use 0 para desativar." preferredPosition="bottom" />}>
                                {editable ?
                                    <Input type="number" min="0" max="1000" value={account?.webhooks?.auto_disable_after_failures ?? 20} onChange={(e) => handleChangeWebhookSettings("auto_disable_after_failures", e.target.value)} />
                                    :
                                    <span>{account?.webhooks?.auto_disable_after_failures ?? 20}</span>
                                }
                            </FormGroup>
                            <FormGroup labelText="E-mail para alertas">
                                {editable ?
                                    <Input type="email" value={account?.webhooks?.alert_email || ""} onChange={(e) => handleChangeWebhookSettings("alert_email", e.target.value)} placeholder="integracoes@empresa.com" />
                                    :
                                    <span>{account?.webhooks?.alert_email || "-"}</span>
                                }
                            </FormGroup>
                        </FormGroupInline>
                        </FormSection>
                    </>
                }
                {hasAdminAccountAccess && activeResource === "partner" &&
                    <>
                        <FormSection title="Relacionamento de parceria" description="Defina quem paga, o nível de acesso do parceiro e a conta responsável.">
                        <FormGroupInline>
                            <FormGroup labelText="É uma conta Parceira?">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsBoolean} onChange={(value) => handleChangeValue("is_partner", value)} selected={+account?.is_partner ?? '0'} />
                                    :
                                    <span>{getOptionByOptionList(+account?.is_partner || '0', optionsBoolean)}</span>
                                }
                            </FormGroup>
                        </FormGroupInline>
                        {Boolean(+account?.is_partner) &&
                            <>
                                <FormGroupInline>
                                    <FormGroup labelText="Pagamento Por">
                                        {editable ?
                                            <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsPaymentFor} onChange={(value) => handleChangeValue("payment_for", value)} selected={account?.payment_for} />
                                            :
                                            <span>{getOptionByOptionList(account?.payment_for, optionsPaymentFor)}</span>
                                        }
                                    </FormGroup>
                                    <FormGroup labelText="Permissões do Parceiro">
                                        {editable ?
                                            <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsPermissionsPartner} onChange={(value) => handleChangeValue("partner_permission", value)} selected={account?.partner_permission} />
                                            :
                                            <span>{getOptionByOptionList(account?.partner_permission, optionsPermissionsPartner)}</span>
                                        }
                                    </FormGroup>
                                </FormGroupInline>
                                {account?.payment_for === "USER" &&
                                    <>
                                        <div className={styles.subsectionHeader}>
                                            <div><strong>Faixas de custo por usuário</strong><span>Defina o valor aplicado conforme a quantidade de usuários.</span></div>
                                            {editable && <Button type="button" variant="secondary" onClick={handleAddNewPaymentUser}><HiPlus /> Adicionar faixa</Button>}
                                        </div>
                                        {paymentByUser && paymentByUser.map((paymentUser, index) => (
                                            <div className={styles.paymentTier} key={index}>
                                                <div className={styles.paymentTierHeader}>
                                                    <strong>Faixa {index + 1}</strong>
                                                    {index > 0 && editable && <Button type="button" variant='secondary' className='action' onClick={(e) => handleRemovePaymentUser(e, index)}><MdOutlineDelete /> Remover</Button>}
                                                </div>
                                                <FormGroupInline>
                                                    <FormGroup labelText="De">
                                                        {editable ?
                                                            <Input type="number" placeholder="Número de usuários" value={index === 0 ? 0 : (paymentByUser[index - 1]?.end_amount ? (parseInt(paymentByUser[index - 1]?.end_amount || 0) + 1) : "")} onChange={(e) => handleChangePaymentUser("start_amount", e.target.value, index)} disabled={true} />
                                                            :
                                                            <span>{index === 0 ? 0 : paymentUser?.start_amount || "-"}</span>
                                                        }
                                                    </FormGroup>
                                                    <FormGroup className={fieldValidationClass(creationValidation.tierErrors[index]?.end_amount)} labelText="Até">
                                                        {editable ?
                                                            <Input type="number" placeholder="Número de usuários" value={paymentUser?.end_amount ?? ""} onChange={(e) => handleChangePaymentUser("end_amount", e.target.value, index)} />
                                                            :
                                                            <span>{paymentUser?.end_amount || "-"}</span>
                                                        }
                                                    </FormGroup>
                                                    <FormGroup className={`${styles.fullWidthField} ${fieldValidationClass(creationValidation.tierErrors[index]?.value)}`} labelText="Custo por usuário">
                                                        {editable ?
                                                            <Input type="currency" placeholder="Custo por usuário" value={paymentUser?.value ?? ""} onChange={(e) => handleChangePaymentUser("value", e.target.value, index)} />
                                                            :
                                                            <span>{paymentUser?.value}</span>
                                                        }
                                                    </FormGroup>
                                                </FormGroupInline>
                                            </div>
                                        ))}
                                    </>
                                }
                            </>
                        }
                        <div className={styles.subsectionHeader}>
                            <div><strong>Conta responsável</strong><span>Parceiro responsável pelo relacionamento desta conta.</span></div>
                        </div>
                        <FormGroupInline>
                            <FormGroup className={fieldValidationClass(creationValidation.settings.partner_of)} labelText="Responsável">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={partnersAccounts} onChange={(value) => handleChangeValue("partner_of", value)} selected={account?.partner_of} />
                                    :
                                    <span>{getOptionByOptionList(account?.partner_of, partnersAccounts) || '-'}</span>
                                }
                            </FormGroup>
                        </FormGroupInline>
                        </FormSection>
                    </>
                }
                {(hasAdminAccountAccess || !editable) && activeResource === "modules" &&
                    <>
                        <FormSection title="Módulos ativos" description={hasAdminAccountAccess ? "Adicione módulos extras à conta. Os módulos incluídos no plano são obrigatórios e não podem ser removidos." : "Consulte os módulos liberados para esta conta."}>
                        <OptionCardSelector
                            options={modules}
                            selected={account?.modules || []}
                            editable={editable}
                            isLocked={module => modulesByPlan.some(moduleId => moduleId?.toString() === module.id?.toString())}
                            onChange={value => handleChangeValue("modules", [...new Set([...modulesByPlan, ...(value || [])].map(String))])}
                            renderIcon={getModuleIcon}
                        />
                        </FormSection>
                    </>
                }
                {hasAdminAccountAccess && activeResource === "chat" &&
                    getOptionByOptionList(account.modules, modules)?.toLowerCase()?.indexOf("chat") !== -1 &&
                    <>
                        <FormSection title="Limites do Chat" description="Configure canais, caixas de entrada e quantidade de usuários disponíveis.">
                        <FormGroupInline>
                            <FormGroup labelText="Limitar Caixas de Entrada?">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsBoolean} onChange={(value) => handleChangeChat("limit_inbox", value)} selected={+account?.chat?.limit_inbox ?? '0'} />
                                    :
                                    <span>{getOptionByOptionList(+account?.chat?.limit_inbox || '0', optionsBoolean)}</span>
                                }
                            </FormGroup>
                            {Boolean(+account?.chat?.limit_inbox) &&
                                <FormGroup labelText="Caixas de Entrada Disponíveis">
                                    {editable ?
                                        <Input type="number" placeholder="Limite de Caixas de Entrada" value={account?.chat?.number_inbox || '0'} onChange={(e) => handleChangeChat("number_inbox", e.target.value)} />
                                        :
                                        <span>{account?.chat?.number_inbox || "-"}</span>
                                    }
                                </FormGroup>
                            }
                            <FormGroup labelText="Limitar Usuários?">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsBoolean} onChange={(value) => handleChangeChat("limit_user", value)} selected={+account?.chat?.limit_user ?? '0'} />
                                    :
                                    <span>{getOptionByOptionList(+account?.chat?.limit_user || '0', optionsBoolean)}</span>
                                }
                            </FormGroup>
                            {Boolean(+account?.chat?.limit_user) &&
                                <FormGroup labelText="Usuários Disponíveis">
                                    {editable ?
                                        <Input type="number" placeholder="Limite de Usuários" value={account?.chat?.number_user || '0'} onChange={(e) => handleChangeChat("number_user", e.target.value)} />
                                        :
                                        <span>{account?.chat?.number_user || "-"}</span>
                                    }
                                </FormGroup>
                            }
                            <FormGroup className={styles.fullWidthField} labelText="Plataformas Disponíveis">
                                <OptionCardSelector
                                    options={optionsPlatform}
                                    selected={account?.chat?.platforms || []}
                                    editable={editable}
                                    onChange={value => handleChangeChat("platforms", value)}
                                    renderIcon={getPlatformIcon}
                                />
                            </FormGroup>
                        </FormGroupInline>
                        </FormSection>
                    </>
                }
                {hasAdminAccountAccess && activeResource === "voip" &&
                    getOptionByOptionList(account.modules, modules)?.toLowerCase()?.indexOf("voip") !== -1 &&
                    <>
                        <FormSection title="Telefonia e transcrição" description="Defina o modelo de custo e os recursos de análise das chamadas.">
                        <FormGroupInline>
                            <FormGroup labelText="Custo Progressivo?">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsBoolean} onChange={(value) => handleChangeVoip("is_progressive_cost", value)} selected={+account?.voip?.is_progressive_cost ?? '0'} />
                                    :
                                    <span>{getOptionByOptionList(+account?.voip?.is_progressive_cost || '0', optionsBoolean)}</span>
                                }
                            </FormGroup>
                        {!Boolean(+account?.voip?.is_progressive_cost) &&
                            <FormGroup className={fieldValidationClass(creationValidation.settings.voip_cost)} labelText="Custo por minuto (Fixo)">
                                    {editable ?
                                        <Input type="currency" placeholder="Custo por minuto" value={account?.voip?.cost_per_minute ?? "0"} onChange={(e) => handleChangeVoip("cost_per_minute", e.target.value)} />
                                        :
                                        <span>{maskCurrency(parseFloat(account?.voip?.cost_per_minute).toFixed(2))}</span>
                                    }
                            </FormGroup>
                        }
                            <FormGroup labelText="Transcrição da Chamada?">
                                {editable ?
                                    <Select multiple={false} name="Selecione uma opção..." placeholder="Buscar..." options={optionsBoolean} onChange={(value) => handleChangeVoip("has_transcription", value)} selected={+account?.voip?.has_transcription ?? '0'} />
                                    :
                                    <span>{getOptionByOptionList(+account?.voip?.has_transcription || '0', optionsBoolean)}</span>
                                }
                            </FormGroup>
                            <FormGroup className={Boolean(+account?.voip?.is_progressive_cost) ? styles.fullWidthField : ""} labelText="Palavras-chave">
                                {editable ?
                                    <Input type="text" placeholder="Palavras-chave" value={account?.voip?.keywords} onChange={(e) => handleChangeVoip("keywords", e.target.value)} />
                                    :
                                    <span>{account?.voip?.keywords || "-"}</span>
                                }
                            </FormGroup>
                        </FormGroupInline>
                        </FormSection>
                    </>
                }
                </>}
                        </section>
                        {isNew &&
                            <footer className={styles.creationActions}>
                                <div>{activeStepIndex > 0 && <Button type="button" variant="secondary" onClick={goToPreviousStep}>Voltar</Button>}</div>
                                <div>
                                    {activeStepIndex < steps.length - 1
                                        ? <Button type="button" variant="primary" onClick={goToNextStep}>Próxima etapa</Button>
                                        : <Button type="submit" variant="primary">Criar conta</Button>
                                    }
                                </div>
                            </footer>
                        }
                    </main>
                </div>
            </form>
        </Modal>
        {pendingNavigation &&
            <ModalConfirm
                icon={<MdWarningAmber />}
                title="Descartar alterações?"
                description={`Você está editando ${steps[activeStepIndex]?.label}${activeStep === "settings" ? ` — ${activeResourceLabel}` : ""}.`}
                text={`As alterações ainda não salvas serão perdidas antes de abrir ${pendingDestination || "a outra seção"}.`}
                confirmLabel="Descartar e continuar"
                confirmVariant="danger"
                closeModal={() => setPendingNavigation(null)}
                onSubmit={confirmNavigation}
            />
        }
        {pendingSubmit &&
            <ModalConfirm
                icon={<MdWarningAmber />}
                title="Confirmar alterações críticas"
                description="Confira cuidadosamente antes de salvar."
                text={
                    <div className={styles.confirmationContent}>
                        <div className={styles.confirmationChanges}>
                            {scopeChanges.map((change, index) => (
                                <div className={styles.confirmationChange} key={`${change.path}-${index}`}>
                                    <strong>{getConfirmationFieldLabel(change.path)}</strong>
                                    <span>{formatConfirmationValue(change.path, change.before, value)} → {formatConfirmationValue(change.path, change.after, account)}</span>
                                </div>
                            ))}
                        </div>
                        {mustProvideReason &&
                            <FormGroup labelText="Justificativa da alteração comercial">
                                <Textarea rows={3} maxLength={500} autoFocus placeholder="Explique por que esta condição está sendo alterada." value={changeReason} onChange={(event) => setChangeReason(event.target.value)} />
                            </FormGroup>
                        }
                    </div>
                }
                confirmLabel="Confirmar e salvar"
                confirmDisabled={mustProvideReason && !changeReason.trim()}
                closeModal={() => setPendingSubmit(null)}
                onSubmit={confirmCriticalSubmit}
            />
        }
    </>)
}

export default ModalAccount
